'use client';

import Box from '@mui/material/Box';
import Slider, { type SliderProps } from '@mui/material/Slider';
import { type SxProps, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { forwardRef, type ReactNode } from 'react';
import { composeSx } from '../../utils/styledUtils';

export interface LabeledSliderProps extends Omit<SliderProps, 'value' | 'onChange' | 'children'> {
  /** Slider label. */
  label: ReactNode;
  /**
   * Small delta hint rendered below the label (e.g. "Δ: $125k"). Use to communicate change from
   * an anchor value (Astor's plan, prior version, etc.).
   */
  delta?: ReactNode;
  /** Tone for the delta hint. Default 'primary'. */
  deltaTone?: 'primary' | 'success' | 'warning' | 'error' | 'neutral';
  /** Current value. */
  value: number;
  /** onChange — fires with the new number. */
  onChange?: (value: number) => void;
  /**
   * Formatted display value rendered to the right of the slider. Pass a string for simple cases
   * or a ReactNode for richer display (e.g. number + small unit suffix).
   */
  displayValue?: ReactNode;
  /** Overall sx for the row container. */
  sx?: SxProps<Theme>;
  /** Width of the label column. Default 140. */
  labelWidth?: number;
  /** Width of the display-value column. Default 120. */
  valueWidth?: number;
}

function deltaColor(tone: NonNullable<LabeledSliderProps['deltaTone']>) {
  if (tone === 'neutral') return 'text.secondary';
  return `${tone}.main`;
}

export const LabeledSlider = forwardRef<HTMLDivElement, LabeledSliderProps>(function LabeledSlider(
  {
    label,
    delta,
    deltaTone = 'primary',
    value,
    onChange,
    displayValue,
    sx,
    labelWidth = 140,
    valueWidth = 120,
    ...sliderProps
  },
  ref,
) {
  const handleChange: SliderProps['onChange'] = (_event, next) => {
    if (typeof next === 'number') {
      onChange?.(next);
    } else if (Array.isArray(next) && typeof next[0] === 'number') {
      onChange?.(next[0]);
    }
  };

  const { sx: sliderSx, ...restSliderProps } = sliderProps as SliderProps;

  return (
    <Box ref={ref} sx={composeSx({ display: 'flex', alignItems: 'center', gap: 2, py: 0.75 }, sx)}>
      <Box sx={{ flexShrink: 0, width: labelWidth, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.primary',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </Typography>
        {delta != null && delta !== '' && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: deltaColor(deltaTone),
              fontWeight: 500,
              fontVariantNumeric: 'tabular-nums',
              mt: 0.25,
            }}
          >
            {delta}
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Slider
          value={value}
          onChange={handleChange}
          size="small"
          {...restSliderProps}
          sx={{
            display: 'block',
            ...sliderSx,
          }}
        />
      </Box>
      {displayValue != null && displayValue !== '' && (
        <Box sx={{ flexShrink: 0, width: valueWidth, textAlign: 'right' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {displayValue}
          </Typography>
        </Box>
      )}
    </Box>
  );
});

LabeledSlider.displayName = 'LabeledSlider';

export default LabeledSlider;
